import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import {
  createFeeDistributionPaymentFile,
  deleteFeeDistributionRecord,
  downloadFeeDistributionExcel,
  getFeeDistributionDebitSettings,
  getFeeDistributionRecord,
  saveFeeDistributionDebitSettings,
} from 'src/shared/services/feeDistributionService';
import { downloadExportFileById } from 'src/shared/services/exportFileService';
import { FeeDistributionDebitSettings, FeeDistributionRecord, FeeDistributionRowStatusEnum } from '../models/feeDistribution';

type FeeDistributionRecordsTableProps = {
  records: FeeDistributionRecord[];
  onChanged: () => void;
};

/**
 * Tidigare låsta fördelningsunderlag. Mottagarraderna hämtas först när man fäller ut ett
 * underlag, eftersom listan annars drar hem allt i onödan.
 */
export const FeeDistributionRecordsTable = (props: FeeDistributionRecordsTableProps) => {
  const { records, onChanged } = props;
  const { t } = useTranslation();

  const [openId, setOpenId] = useState<number | undefined>(undefined);
  const [detail, setDetail] = useState<FeeDistributionRecord | undefined>(undefined);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const [deleteRecord, setDeleteRecord] = useState<FeeDistributionRecord | undefined>(undefined);
  const [fileRecord, setFileRecord] = useState<FeeDistributionRecord | undefined>(undefined);
  const [payDate, setPayDate] = useState<string>(DateHelper.getDate(new Date()));
  const [statementText, setStatementText] = useState<string>('');
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const [busyId, setBusyId] = useState<number | undefined>(undefined);

  // Avsändaren i filen. Uppgifterna finns bara i databasen och har inget eget gränssnitt, så de
  // visas och sparas här, där de faktiskt används.
  const [debitSettings, setDebitSettings] = useState<FeeDistributionDebitSettings>({});
  const [isLoadingSettings, setIsLoadingSettings] = useState<boolean>(false);

  const openFileDialog = async (record: FeeDistributionRecord) => {
    setPayDate(DateHelper.getDate(new Date()));
    setFileRecord(record);
    setIsLoadingSettings(true);

    try {
      const res = await getFeeDistributionDebitSettings();
      setDebitSettings(res.data ?? {});
    } catch (error) {
      ConsoleHelper.log(error);
      setDebitSettings({});
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const debitSettingsComplete = !!debitSettings.orgName?.trim() && !!debitSettings.orgNumber?.trim() && !!debitSettings.account?.trim();

  const downloadExcel = async (record: FeeDistributionRecord) => {
    setBusyId(record.id);

    try {
      const res = await downloadFeeDistributionExcel(record.id);

      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('download', `Fordelningsunderlag-${record.number}.xlsx`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      ConsoleHelper.log(error);
      toast.error(<>{t('invoice:paymentDistribution.excelError')}</>);
    } finally {
      setBusyId(undefined);
    }
  };

  const downloadPaymentFile = async (record: FeeDistributionRecord) => {
    if (!record.paymentFileId) return;

    setBusyId(record.id);

    try {
      const res = await downloadExportFileById(record.paymentFileId, 'application/xml');

      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('download', record.fileName || `Fordelningsunderlag-${record.number}.xml`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      ConsoleHelper.log(error);
      toast.error(<>{t('invoice:paymentDistribution.paymentFileError')}</>);
    } finally {
      setBusyId(undefined);
    }
  };

  const createFile = async () => {
    if (!fileRecord) return;

    setIsWorking(true);

    try {
      await saveFeeDistributionDebitSettings(debitSettings);
      await createFeeDistributionPaymentFile(fileRecord.id, payDate, statementText);
      toast.success(<>{t('invoice:paymentDistribution.paymentFileSuccess')}</>);
      setFileRecord(undefined);
      setStatementText('');
      onChanged();
    } catch (error) {
      ConsoleHelper.log(error);
      toast.error(<>{t('invoice:paymentDistribution.paymentFileError')}</>);
    } finally {
      setIsWorking(false);
    }
  };

  const removeRecord = async () => {
    if (!deleteRecord) return;

    setIsWorking(true);

    try {
      await deleteFeeDistributionRecord(deleteRecord.id);
      toast.success(<>{t('invoice:paymentDistribution.deleteRecordSuccess')}</>);
      setDeleteRecord(undefined);
      onChanged();
    } catch (error) {
      ConsoleHelper.log(error);
      toast.error(<>{t('invoice:paymentDistribution.deleteRecordError')}</>);
    } finally {
      setIsWorking(false);
    }
  };

  const toggle = async (id: number) => {
    if (openId === id) {
      setOpenId(undefined);
      return;
    }

    setOpenId(id);
    setDetail(undefined);
    setIsLoadingDetail(true);

    try {
      const res = await getFeeDistributionRecord(id);
      setDetail(res.data);
    } catch (error) {
      ConsoleHelper.log(error);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const statusChip = (status: number) => {
    if (status === FeeDistributionRowStatusEnum.Paid)
      return <CustomChip size='small' color='success' label={t('invoice:paymentDistribution.statusPaid')} smallRadius />;
    if (status === FeeDistributionRowStatusEnum.NoAccount)
      return <CustomChip size='small' color='warning' label={t('invoice:paymentDistribution.statusNoAccount')} smallRadius />;
    if (status === FeeDistributionRowStatusEnum.NotPaidOut)
      return <CustomChip size='small' color='grey' label={t('invoice:paymentDistribution.countryPartInfo')} smallRadius />;

    return <CustomChip size='small' color='info' label={t('invoice:paymentDistribution.statusIncluded')} smallRadius />;
  };

  const accountText = (row: FeeDistributionRecord['rows'][0]) => {
    if (row.bankGiro) return t('invoice:paymentDistribution.accountBankGiro', { value: row.bankGiro });
    if (row.postGiro) return t('invoice:paymentDistribution.accountPostGiro', { value: row.postGiro });

    return undefined;
  };

  const table = (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>{t('invoice:paymentDistribution.recordNumber')}</TableCell>
          <TableCell>{t('invoice:paymentDistribution.period')}</TableCell>
          <TableCell>{t('invoice:paymentDistribution.created')}</TableCell>
          <TableCell align='right'>{t('invoice:paymentDistribution.payments')}</TableCell>
          <TableCell align='right'>{t('invoice:paymentDistribution.amount')}</TableCell>
          <TableCell>{t('invoice:paymentDistribution.paymentFile')}</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((record) => (
          <Fragment key={record.id}>
            <TableRow>
              <TableCell>{record.number}</TableCell>
              <TableCell>
                {t('invoice:paymentDistribution.periodBetween', {
                  from: DateHelper.getDate(record.dateFrom),
                  to: DateHelper.getDate(record.dateTo),
                })}
              </TableCell>
              <TableCell>{DateHelper.getDate(record.created)}</TableCell>
              <TableCell align='right'>{record.paymentCount}</TableCell>
              <TableCell align='right'>
                <AmountWithCurrency amount={record.totalAmount} />
              </TableCell>
              <TableCell>
                {record.fileName ? (
                  <CustomChip size='small' color='success' label={t('invoice:paymentDistribution.statusPaid')} smallRadius />
                ) : (
                  <CustomChip size='small' color='grey' label={t('invoice:paymentDistribution.noFile')} smallRadius />
                )}
              </TableCell>
              <TableCell align='right'>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <LoadingButton
                    size='small'
                    variant='text'
                    loading={busyId === record.id}
                    disabled={busyId === record.id}
                    onClick={() => downloadExcel(record)}
                  >
                    {t('invoice:paymentDistribution.excel')}
                  </LoadingButton>
                  {record.fileName ? (
                    <LoadingButton
                      size='small'
                      variant='text'
                      loading={busyId === record.id}
                      disabled={busyId === record.id}
                      onClick={() => downloadPaymentFile(record)}
                    >
                      {t('invoice:paymentDistribution.downloadFile')}
                    </LoadingButton>
                  ) : (
                    <Button size='small' variant='text' disabled={busyId === record.id} onClick={() => openFileDialog(record)}>
                      {t('invoice:paymentDistribution.createFile')}
                    </Button>
                  )}
                  {!record.fileName && (
                    <IconButton size='small' color='error' disabled={busyId === record.id} onClick={() => setDeleteRecord(record)}>
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  )}
                  <IconButton size='small' onClick={() => toggle(record.id)}>
                    {openId === record.id ? <KeyboardArrowUp fontSize='small' /> : <KeyboardArrowDown fontSize='small' />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                <Collapse in={openId === record.id} unmountOnExit>
                  <Box sx={{ py: 1.5 }}>
                    {isLoadingDetail && <Skeleton variant='text' sx={{ height: '40px' }} />}
                    {!isLoadingDetail && detail && detail.id === record.id && (
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('invoice:paymentDistribution.recipient')}</TableCell>
                            <TableCell>{t('invoice:paymentDistribution.orgNr')}</TableCell>
                            <TableCell>{t('invoice:paymentDistribution.account')}</TableCell>
                            <TableCell align='right'>{t('invoice:paymentDistribution.payments')}</TableCell>
                            <TableCell align='right'>{t('invoice:paymentDistribution.amount')}</TableCell>
                            <TableCell>{t('invoice:paymentDistribution.status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detail.rows.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.name || <em>{t('invoice:paymentDistribution.recipientMissing')}</em>}</TableCell>
                              <TableCell>{row.orgNr}</TableCell>
                              <TableCell>{accountText(row) || <em>{t('invoice:paymentDistribution.accountMissing')}</em>}</TableCell>
                              <TableCell align='right'>{row.paymentCount}</TableCell>
                              <TableCell align='right'>
                                <AmountWithCurrency amount={row.amount} />
                              </TableCell>
                              <TableCell>{statusChip(row.status)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {!isLoadingDetail && detail && detail.id === record.id && detail.rows.length === 0 && (
                      <Typography variant='body2' color='text.secondary'>
                        {t('invoice:paymentDistribution.noRecordRows')}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      {table}

      <GenericDialog
        open={!!fileRecord}
        maxWidth='sm'
        dialogTitle={t('invoice:paymentDistribution.fileDialogTitle')}
        dialogContent={
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                {t('invoice:paymentDistribution.debitTitle')}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('invoice:paymentDistribution.debitInfo')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label={t('invoice:paymentDistribution.debitOrgName')}
                value={debitSettings.orgName ?? ''}
                onChange={(event) => setDebitSettings((prev) => ({ ...prev, orgName: event.target.value }))}
                disabled={isLoadingSettings}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label={t('invoice:paymentDistribution.debitOrgNumber')}
                value={debitSettings.orgNumber ?? ''}
                onChange={(event) => setDebitSettings((prev) => ({ ...prev, orgNumber: event.target.value }))}
                disabled={isLoadingSettings}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label={t('invoice:paymentDistribution.debitAccount')}
                value={debitSettings.account ?? ''}
                onChange={(event) => setDebitSettings((prev) => ({ ...prev, account: event.target.value }))}
                disabled={isLoadingSettings}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} />
            <Grid item xs={12} sm={6}>
              <DatePicker
                format='YYYY-MM-DD'
                label={t('invoice:paymentDistribution.payDate')}
                value={dayjs(payDate)}
                onChange={(date) => {
                  if (date && date.isValid()) setPayDate(date.format('YYYY-MM-DD'));
                }}
                slots={{
                  textField: (params) => <TextField {...params} size='small' InputLabelProps={{ shrink: true }} fullWidth />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label={t('invoice:paymentDistribution.statementText')}
                value={statementText}
                onChange={(event) => setStatementText(event.target.value)}
                placeholder={t('invoice:paymentDistribution.statementTextDefault')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary'>
                {t('invoice:paymentDistribution.fileDialogInfo')}
              </Typography>
            </Grid>
          </Grid>
        }
        dialogActions={
          <>
            <Button variant='outlined' color='primary' disableElevation disabled={isWorking} onClick={() => setFileRecord(undefined)}>
              {t('common:button.cancel')}
            </Button>
            <LoadingButton
              variant='contained'
              disableElevation
              loading={isWorking}
              disabled={isWorking || isLoadingSettings || !debitSettingsComplete}
              onClick={createFile}
            >
              {t('common:button.confirm')}
            </LoadingButton>
          </>
        }
        onSave={createFile}
        onClose={() => {
          if (!isWorking) setFileRecord(undefined);
        }}
      />

      <GenericDialog
        open={!!deleteRecord}
        maxWidth='sm'
        dialogTitle={t('invoice:paymentDistribution.deleteRecordTitle')}
        dialogContent={
          <Typography variant='body2'>
            {t('invoice:paymentDistribution.deleteRecordConfirm', {
              number: deleteRecord?.number ?? 0,
              count: deleteRecord?.paymentCount ?? 0,
            })}
          </Typography>
        }
        dialogActions={
          <>
            <Button variant='outlined' color='primary' disableElevation disabled={isWorking} onClick={() => setDeleteRecord(undefined)}>
              {t('common:button.cancel')}
            </Button>
            <LoadingButton variant='contained' color='error' disableElevation loading={isWorking} disabled={isWorking} onClick={removeRecord}>
              {t('common:button.confirm')}
            </LoadingButton>
          </>
        }
        onSave={removeRecord}
        onClose={() => {
          if (!isWorking) setDeleteRecord(undefined);
        }}
      />
    </>
  );
};
